import React, { useEffect } from 'react'
import UtmUtils from '../utils/utmUtils'
import qs from 'qs'
import { v4 } from 'uuid'
import { useSearchParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { Splash } from '../components/Splash'
import { selectCurrentStore, storeSetter } from '../reducers/storeSlice'
import {
  setLanguage,
  selectActiveCallAction,
  CALL_ACTION_PRODUCTS,
  CALL_ACTION_CHAT,
  CALL_ACTION_CAMERAS,
} from '../reducers/uiSlice'
import PermissionDeniedDialog from '../components/dialogs/permissionDeniedDialog'
import { useTranslation } from 'react-i18next'
import { useStore as useStoreState, useViews } from 'state'
import { useStore as useStoreFetch } from '../hooks/useStore'
import { useAuth } from '@gojiraf/auth'
import { useDevices } from '@gojiraf/responsive'
import StoreNotFound from './storeNotFound'
import { Call } from '../components/Call/Call'
import { useEventById } from '../hooks/use-event-by-id'
import { useCanJoinEvent } from '../hooks/useCanJoinEvent'
import { useUtm } from '@gojiraf/useutm'
import { AutoPlayUnlocker } from '../components/AutoPlayUnlocker'
import { BackgroundImageWithFallback } from '../components/BackgroundImageWithFallback'
import fallbackBackground from '../../assets/fallback_background.jpeg'
import { TencentConnectionDialog } from '../components/dialogs/TencentConnectionDialog'
import { DebugLogProvider, useDebugLog } from '../hooks/useDebugLog'
import { DebugLogDialog } from '../components/DebugLogDialog'

export function LiveEvent() {
  const { isLoading: isLoadingEvent, data: event } = useEventById()
  const { isDesktop } = useDevices()
  const { send } = useViews()
  const { i18n } = useTranslation()
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const browserLang = searchParams.get('lang')
  const activeCallAction = useSelector(selectActiveCallAction)
  const {
    setUsername,
    user,
    eventId: authStoredEventId,
    setEventId: setAuthEventId,
    clearAuth,
    setCreds,
  } = useAuth()
  const { isLoading: isLoadingStore } = useStoreFetch()
  const { store } = useStoreState()
  const reduxStore = useSelector(selectCurrentStore)
  const isDebugStore = reduxStore?.storeConfigurations?.debug
  const { canBypassJoin } = useCanJoinEvent()
  const queryParams = qs.parse(window.location.search, { ignoreQueryPrefix: true })
  const { utm_medium } = UtmUtils.getUtmObject(queryParams)
  const { showUIComponents } = useUtm(utm_medium)

  useEffect(() => {
    if (event?.eventId && event?.eventId !== authStoredEventId) {
      if (authStoredEventId) {
        clearAuth()
        setCreds({ id: v4() })
      }
      setAuthEventId(event?.eventId)
    }
  }, [event?.eventId, authStoredEventId])

  useEffect(() => {
    if (isLoadingStore || isLoadingEvent) {
      return
    }

    if (store.lang) {
      i18n.changeLanguage(store.lang)
      dispatch(setLanguage(store.lang))
    }

    dispatch(
      storeSetter({
        store,
        isMobile: !isDesktop,
        browserLang,
        autoAcceptTermsAndConditions: !showUIComponents,
      }),
    )

    if (user.name === 'Anonymous' || user.name === 'Anónimo') setUsername(null)
  }, [store, isLoadingStore, isLoadingEvent])

  useEffect(() => {
    if (activeCallAction === CALL_ACTION_PRODUCTS) send({ type: 'SHOW_PRODUCTS' })
    if (activeCallAction === CALL_ACTION_CHAT) send({ type: 'SHOW_CHAT' })
    if (activeCallAction === CALL_ACTION_CAMERAS) send({ type: 'SHOW_PARTICIPANTS' })
  }, [activeCallAction])

  if (
    reduxStore === null ||
    isLoadingStore === true ||
    isLoadingEvent === true ||
    (event.status !== 'STARTED' && canBypassJoin === false)
  ) {
    return <Splash />
  }

  // Esto deberia ser un redirect
  if (store.error) {
    return <StoreNotFound />
  }

  return (
    <DebugLogProvider enabled={isDebugStore}>
      <AutoPlayUnlocker>
        <BackgroundImageWithFallback
          desktop={event.images?.['countdown-desktop']}
          mobile={event.images?.['countdown-mobile']}
          fallback={fallbackBackground}
        >
          <Call />
        </BackgroundImageWithFallback>
        <TencentConnectionDialog />
        <PermissionDeniedDialog />
      </AutoPlayUnlocker>
      {isDebugStore && <DebugLogOverlay />}
    </DebugLogProvider>
  )
}

function DebugLogOverlay() {
  const { logs } = useDebugLog()
  return <DebugLogDialog logs={logs} title="Debug Logs" />
}
